import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { Application } from 'app/models/dto/deal-application.model';
import { Deal } from 'app/models/dto/deal.model';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Offer } from 'app/services/offers/offer.model';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { ApplicationState } from 'app/store/deal/application/application.state';
import { DealAlertsState } from 'app/store/deal/deal-alerts/deal-alerts.state';
import { GetDealQuestionRequirements } from 'app/store/deal/deal-application-requirements/deal-application-requirement.actions';
import { DealApplicationQuestionRequirement, DealApplicationRequirementState } from 'app/store/deal/deal-application-requirements/deal-application-requirement.state';
import { DealDocumentState } from 'app/store/deal/deal-documents/deal-document.state';
import { GetLenderDealStatus, SaveDealStatus } from 'app/store/deal/deal/deal.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { OfferState } from 'app/store/offers/offer.state';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { ApprovalsState } from 'ng2-app/store/offers/approvals.state';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { OfferOptionsState } from 'app/store/offers/offer-options.state';
import { DealState } from 'app/store/deal/deal/deal.state';
import { OffersService } from 'app/services/offers/offers.service';

@Component({
  selector: 'app-deal-smart',
  templateUrl: './deal-smart.component.html',
  styleUrls: ['./deal-smart.component.scss']
})
export class DealSmartComponent implements OnInit, OnDestroy {

  @Input() bulkSubmitting = false;
  @Input() deal: Deal;
  @Input() readOnly: boolean = false;
  @Input() showDealRightColumn: boolean = true;

  @Select(ApprovalsState.approvals) allApprovals$: Observable<Approval[]>;
  @Select(ApprovalsState.loading) approvalsLoading$: Observable<boolean>;
  @Select(DealState.deals) deals$: Observable<Deal[]>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(OfferOptionsState.offerOptions) offerOptions$: Observable<OfferOption[] | null>;
  @Select(OfferState.dealOffers) offers$: Observable<Offer[]>;

  @Output() expand = new EventEmitter();
  @Output() submit = new EventEmitter<boolean>();

  approvals$: Observable<Approval[]>;
  dealOffers$: Observable<Offer[]>;
  appQuestionsLoading$: Observable<boolean>;
  canReactivateDeals$: Observable<boolean>;
  dealApplications$: Observable<Application[]>;
  dealApplicationQuestions$: Observable<DealApplicationQuestionRequirement | null>;
  destroyed$ = new Subject<boolean>();
  dateSigned$: Observable<BorrowerValue | null>;
  hasSignedApplication$: Observable<boolean | undefined>;
  questionRequirementsMet$: Observable<boolean>;
  user: LenderUser;
  combinedOfferOptionData$: Observable<OfferOption[] | null>;

  constructor(
    private store: Store,
    private offersService: OffersService
  ) { }

  ngOnInit() {
    this.approvals$ = this.store.select(ApprovalsState.forDeal).pipe(map(filterFn => filterFn(this.deal.id)));
    this.store.dispatch(new GetLenderDealStatus(this.deal));
    this.store.dispatch(new GetDealQuestionRequirements(this.deal));
    this.canReactivateDeals$ = this.lenderUser$.pipe(
      map(currentUser => currentUser.permissions.includes('deal.reactivate'))
    );
    this.dealOffers$ = this.store.select(OfferState.dealOffers)
      .pipe(map(filterFn => filterFn(this.deal.id, this.deal.stage)));
    this.dealApplicationQuestions$ = this.store.select(DealApplicationRequirementState.dealQuestionRequirements)
      .pipe(map(filterFn => filterFn(this.deal.id)));
    this.dealApplications$ = this.store.select(ApplicationState.dealApplications)
      .pipe(map(filterFn => filterFn(this.deal.id)));
    this.appQuestionsLoading$ = this.store.select(DealApplicationRequirementState.loading);
    this.dateSigned$ = this.store.select(BorrowerValueState.valueByAlias).pipe(map(filterFn => filterFn('date_signed')));
    this.hasSignedApplication$ = this.store.select(DealDocumentState.hasSignedApplication)
      .pipe(map(filterFn => filterFn(this.deal.id)));
    this.questionRequirementsMet$ = this.store.select(DealApplicationRequirementState.requirementsMet)
      .pipe(map(filterFn => filterFn(this.deal.id)));
    this.store.select(DealAlertsState.dealAlerts).pipe(map(filterFn => filterFn(this.deal.id)))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(errors => {
        if (errors.length > 0) {
          this.expand.emit();
        }
      });

    this.combinedOfferOptionData$ = combineLatest([this.offerOptions$, this.deals$, this.allApprovals$, this.offers$])
      .pipe(
        takeUntil(this.destroyed$),
        map(([offerOptions, deals, approvals, offers]) => {
          return this.offersService.aggregateOfferOptionRelationships(offerOptions, deals, approvals, offers);
        })
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  updateToAppComplete() {
    this.store.dispatch(new SaveDealStatus(this.deal.id, 'application', 'appComplete', '', [], []));
  }
}
